import { somar, vetor, type Vetor3 } from '../geometria/vetor3'
import { corpoSoltoPorId, type CorpoSolto, type OrigemCarga } from './cargaSolta'
import type { Ocupante } from './ocupantes'
import { Y_BASE_LATERAL, type PerfilVeiculo } from './perfilVeiculo'

/**
 * Onde cada carga solta começa o acidente.
 *
 * Existe porque `origemPadrao` do catálogo era campo morto: toda carga solta
 * nascia em `perfil.ancoras[0]` — a âncora do motorista —, então a caixa de
 * ferramentas do porta-malas e o gato do colo partiam do mesmo ponto, em cima da
 * vítima, sobrepostos um ao outro. Aqui cada compartimento vira um ponto de
 * verdade do habitáculo, derivado do `PerfilVeiculo` e de mais nada.
 *
 * Módulo de domínio: TypeScript puro, sem React e sem Three.js. A cena só lê os
 * pontos que saem daqui, para que "de onde o objeto parte" e "o que está
 * desenhado ali" sejam a mesma geometria — o mesmo invariante de
 * `superficiesInternas.ts`.
 */

/** Folga mínima entre dois corpos no mesmo compartimento, em metros. */
export const FOLGA_ENTRE_CORPOS = 0.04

/** Quanto o forro rouba da largura total da carroceria, de cada lado. */
const ESPESSURA_FORRO = 0.12

/**
 * Um lugar do habitáculo onde carga solta pode estar.
 *
 * `centro` é o ponto da SUPERFÍCIE DE APOIO (assento, assoalho, tampo do
 * painel, coxas), não o centro do volume: cada corpo é apoiado sobre ele somando
 * a própria meia-altura. `largura`/`profundidade` são a extensão útil em X e Z,
 * e é o que faz o empacotamento saber quando mudar de fileira.
 */
export interface Compartimento {
  id: OrigemCarga
  nome: string
  centro: Vetor3
  largura: number
  profundidade: number
}

const meiaLarguraInterna = (perfil: PerfilVeiculo): number =>
  perfil.largura / 2 - ESPESSURA_FORRO

const ancoraDaFila = (perfil: PerfilVeiculo, fila: 'dianteira' | 'traseira') =>
  perfil.ancoras.find((a) => a.fila === fila) ?? perfil.ancoras[0]!

/**
 * Os cinco compartimentos do perfil, em coordenadas do veículo.
 *
 * As medidas saem das cotas que o perfil já declara — `pisoY`, `cinturaY`,
 * `colunaA`, `traseiraZ`, `alturaAssento` das âncoras — e nunca de números
 * escritos à mão na cena.
 */
export function compartimentosDeCarga(perfil: PerfilVeiculo): Compartimento[] {
  const dianteira = ancoraDaFila(perfil, 'dianteira')
  const traseira = ancoraDaFila(perfil, 'traseira')
  const meia = meiaLarguraInterna(perfil)

  // o porta-malas vai de atrás do banco traseiro até um vão antes do para-choque
  const portaMalasDianteiro = traseira.posicao.z - 0.3
  const portaMalasTraseiro = perfil.traseiraZ + 0.12

  return [
    {
      id: 'colo',
      nome: 'No colo do ocupante',
      // as coxas, à frente da pelve: é o lugar do animal de estimação solto
      centro: vetor(dianteira.posicao.x, dianteira.posicao.y + 0.1, dianteira.posicao.z + 0.18),
      largura: 0.36,
      profundidade: 0.3,
    },
    {
      id: 'bancoTraseiro',
      nome: 'Sobre o banco traseiro',
      centro: vetor(0, traseira.alturaAssento, traseira.posicao.z),
      largura: meia * 2,
      profundidade: 0.42,
    },
    {
      id: 'assoalho',
      nome: 'No assoalho, aos pés',
      centro: vetor(0, perfil.pisoY, dianteira.posicao.z + 0.42),
      largura: meia * 1.4,
      profundidade: 0.35,
    },
    {
      id: 'painel',
      nome: 'Sobre o painel',
      // tampo do painel, logo abaixo da base do para-brisa
      centro: vetor(0, perfil.cinturaY, perfil.colunaA.zBase - 0.14),
      largura: meia * 1.5,
      profundidade: 0.22,
    },
    {
      id: 'portaMalas',
      nome: 'No porta-malas',
      centro: vetor(0, perfil.pisoY + 0.02, (portaMalasDianteiro + portaMalasTraseiro) / 2),
      largura: meia * 1.5,
      profundidade: Math.abs(portaMalasDianteiro - portaMalasTraseiro),
    },
  ]
}

export function compartimentoPorId(perfil: PerfilVeiculo, id: OrigemCarga): Compartimento {
  const encontrado = compartimentosDeCarga(perfil).find((c) => c.id === id)
  if (!encontrado) throw new Error(`Compartimento de carga desconhecido: ${id}`)
  return encontrado
}

/** Envelope do habitáculo, para o teste conferir que nenhum compartimento sai dele. */
export function dentroDoEnvelope(perfil: PerfilVeiculo, ponto: Vetor3): boolean {
  return (
    Math.abs(ponto.x) <= perfil.largura / 2 &&
    ponto.y >= Y_BASE_LATERAL &&
    ponto.y <= perfil.tetoY &&
    ponto.z <= perfil.frenteZ &&
    ponto.z >= perfil.traseiraZ
  )
}

export interface ItemCarga {
  /** id do ocupante, para a cena reencontrar a posição do corpo que desenha */
  id: string
  corpo: CorpoSolto
  compartimento: OrigemCarga
}

export interface ColocacaoCarga {
  id: string
  /** centro do volume do corpo, em coordenadas do veículo */
  posicao: Vetor3
}

/**
 * Posição inicial de cada carga solta, sem sobreposição.
 *
 * Recebe a LISTA inteira, não um corpo por vez, porque garantir que dois corpos
 * não se sobreponham depende das dimensões de ambos: uma treliça de passo fixo
 * ou acomoda o cachorro e desperdiça o espaço do notebook, ou acomoda o notebook
 * e deixa o cachorro invadindo o vizinho. Com a lista em mão o empacotamento usa
 * a dimensão real de cada um.
 *
 * O arranjo é determinístico e centrado: um corpo sozinho fica no meio do
 * compartimento, e a ordem da lista decide o resto. Nada de aleatório — o mesmo
 * cenário tem de montar a mesma cena a cada execução.
 */
export function colocarCargasSoltas(
  perfil: PerfilVeiculo,
  itens: readonly ItemCarga[],
): ColocacaoCarga[] {
  const saida: ColocacaoCarga[] = []

  for (const compartimento of compartimentosDeCarga(perfil)) {
    const doCompartimento = itens.filter((i) => i.compartimento === compartimento.id)
    if (doCompartimento.length === 0) continue
    saida.push(...empacotar(compartimento, doCompartimento))
  }

  // um id com compartimento inválido não pode simplesmente desaparecer da cena
  const colocados = new Set(saida.map((c) => c.id))
  for (const item of itens) {
    if (!colocados.has(item.id)) {
      const centro = compartimentoPorId(perfil, 'assoalho').centro
      saida.push({ id: item.id, posicao: apoiar(centro, item.corpo, 0, 0) })
    }
  }

  return saida
}

/** Apoia o corpo sobre a superfície do compartimento, deslocado na horizontal. */
function apoiar(centro: Vetor3, corpo: CorpoSolto, dx: number, dz: number): Vetor3 {
  return somar(centro, vetor(dx, corpo.dimensoes.y / 2, dz))
}

/** Extensão total de uma sequência de corpos num eixo, já com as folgas. */
const extensao = (medidas: readonly number[]): number =>
  medidas.reduce((soma, m) => soma + m, 0) + FOLGA_ENTRE_CORPOS * (medidas.length - 1)

/** Fatia a sequência em grupos que caibam em `limite`, pela medida de cada item. */
function fatiar<T>(itens: readonly T[], medida: (item: T) => number, limite: number): T[][] {
  const grupos: T[][] = []
  let atual: T[] = []

  for (const item of itens) {
    const comEste = extensao([...atual.map(medida), medida(item)])
    // um item maior que o limite ainda entra: sozinho no seu grupo
    if (atual.length > 0 && comEste > limite) {
      grupos.push(atual)
      atual = []
    }
    atual.push(item)
  }
  if (atual.length > 0) grupos.push(atual)

  return grupos
}

/**
 * Fileiras ao longo de X, novas fileiras em Z, camadas empilhadas em Y quando o
 * compartimento enche.
 *
 * Em dois passes — fatiar e só então posicionar — porque centrar exige conhecer a
 * extensão total antes de colocar o primeiro corpo. Posicionando em um pass só, a
 * primeira fileira encosta na borda do compartimento em vez de ficar no meio, e o
 * caso comum (um objeto sozinho) sai visivelmente torto.
 */
function empacotar(compartimento: Compartimento, itens: readonly ItemCarga[]): ColocacaoCarga[] {
  const fileiras = fatiar(itens, (i) => i.corpo.dimensoes.x, compartimento.largura)
  const profundidadeDa = (fileira: readonly ItemCarga[]) =>
    Math.max(...fileira.map((i) => i.corpo.dimensoes.z))
  const camadas = fatiar(fileiras, profundidadeDa, compartimento.profundidade)

  const saida: ColocacaoCarga[] = []
  let alturaAcumulada = 0

  for (const camada of camadas) {
    const profundidadeTotal = extensao(camada.map(profundidadeDa))
    let z = -profundidadeTotal / 2

    for (const fileira of camada) {
      const profundidade = profundidadeDa(fileira)
      const larguraTotal = extensao(fileira.map((i) => i.corpo.dimensoes.x))
      let x = -larguraTotal / 2
      z += profundidade / 2

      for (const item of fileira) {
        const meia = item.corpo.dimensoes.x / 2
        x += meia
        const base = somar(compartimento.centro, vetor(0, alturaAcumulada, 0))
        saida.push({ id: item.id, posicao: apoiar(base, item.corpo, x, z) })
        x += meia + FOLGA_ENTRE_CORPOS
      }

      z += profundidade / 2 + FOLGA_ENTRE_CORPOS
    }

    const alturaDaCamada = Math.max(
      ...camada.flat().map((i) => i.corpo.dimensoes.y),
    )
    alturaAcumulada += alturaDaCamada
  }

  return saida
}

/**
 * Os itens de carga de uma lista de ocupantes, na forma que o empacotamento pede.
 *
 * O compartimento do ocupante manda; sem ele vale o `origemPadrao` do catálogo.
 * Pessoas não entram: elas partem da própria âncora.
 */
export function itensDeCarga(lista: readonly Ocupante[]): ItemCarga[] {
  return lista
    .filter((o) => o.tipo !== 'pessoa')
    .map((o) => {
      const corpo = corpoSoltoPorId(o.idPerfil)
      return { id: o.id, corpo, compartimento: o.compartimento ?? corpo.origemPadrao }
    })
}

/** Posição inicial de cada carga solta, indexada pelo id do ocupante. */
export function origensDaCarga(
  perfil: PerfilVeiculo,
  lista: readonly Ocupante[],
): Map<string, Vetor3> {
  return new Map(colocarCargasSoltas(perfil, itensDeCarga(lista)).map((c) => [c.id, c.posicao]))
}
